import { ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from "recharts";

const AXIS = { fontSize: 10, tick: { fill: "#94a3b8" }, axisLine: { stroke: "#e2e8f0" }, tickLine: false };

// Monthly actual/forecast line chart: solid = actual, dashed = forecast,
// one shared boundary point keeps the series continuous (§7).
export function AfLineChart({ months, series, targets = [], formatter = (v) => v, testId }) {
  const boundary = months.reduce((acc, m) => (m.state === "ACTUAL" ? m.month : acc), 0);
  const data = months.map((m) => {
    const row = { name: m.label };
    for (const s of series) {
      row[`${s.key}_a`] = m.month <= boundary ? m[s.key] : null;
      row[`${s.key}_f`] = m.month >= boundary ? m[s.key] : null;
    }
    return row;
  });
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} data-testid={testId}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...AXIS} interval={1} />
        <YAxis {...AXIS} width={44} tickFormatter={formatter} />
        <Tooltip formatter={(v) => (v == null ? "N/A" : formatter(v))} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        {series.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {targets.filter((t) => t.value != null).map((t) => (
          <ReferenceLine key={t.label} y={t.value} stroke={t.color} strokeDasharray="6 4" label={{ value: t.label, fontSize: 9, fill: t.color, position: "insideTopRight" }} />
        ))}
        {series.map((s) => (
          <Line key={`${s.key}_a`} type="monotone" dataKey={`${s.key}_a`} name={s.label} stroke={s.color} strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
        ))}
        {series.map((s) => (
          <Line key={`${s.key}_f`} type="monotone" dataKey={`${s.key}_f`} name={`${s.label} (forecast)`} stroke={s.color} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} legendType="none" />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function CashChart({ months, testId }) {
  const boundary = months.reduce((acc, m) => (m.state === "ACTUAL" ? m.month : acc), 0);
  const data = months.map((m) => ({
    name: m.label,
    net: m.net_cash_flow,
    closing_a: m.month <= boundary ? m.closing_cash : null,
    closing_f: m.month >= boundary ? m.closing_cash : null,
  }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} data-testid={testId}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...AXIS} interval={1} />
        <YAxis {...AXIS} width={52} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
        <Tooltip formatter={(v) => (v == null ? "N/A" : `${new Intl.NumberFormat("sv-SE").format(Math.round(v))} kr`)} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="net" name="Net Cash Flow" fill="#93c5fd" radius={[3, 3, 0, 0]} isAnimationActive={false} />
        <Line type="monotone" dataKey="closing_a" name="Closing Cash" stroke="#0052CC" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
        <Line type="monotone" dataKey="closing_f" name="Closing Cash (forecast)" stroke="#0052CC" strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} legendType="none" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function StepChart({ months, dataKey, label, testId }) {
  const data = months.map((m) => ({ name: m.label, value: m[dataKey] }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} data-testid={testId}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...AXIS} interval={1} />
        <YAxis {...AXIS} width={32} allowDecimals={false} />
        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        <Line type="stepAfter" dataKey="value" name={label} stroke="#0052CC" strokeWidth={2} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function AnnualBars({ rows, dataKey, label, formatter = (v) => v, testId, kindColors }) {
  const colors = kindColors || { ACTUAL: "#0052CC", EXPECTED: "#38bdf8", FORECAST: "#cbd5e1" };
  const data = rows.map((r) => ({ ...r, value: r[dataKey], fill: colors[r.kind] || "#0052CC" }));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} data-testid={testId}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="year" {...AXIS} />
        <YAxis {...AXIS} width={44} tickFormatter={formatter} />
        <Tooltip formatter={(v, n, item) => [`${formatter(v)} (${item.payload.kind.toLowerCase()})`, label]} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        <Bar dataKey="value" name={label} radius={[3, 3, 0, 0]} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function CompanyBars({ rows, series, formatter = (v) => v, testId }) {
  const data = rows.map((r) => ({ name: r.company_name.replace("Elva ", "").replace(" AB", ""), ...r }));
  const colors = ["#0052CC", "#38bdf8", "#f59e0b", "#94a3b8"];
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} data-testid={testId}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="name" {...AXIS} />
        <YAxis {...AXIS} width={44} tickFormatter={formatter} />
        <Tooltip formatter={(v) => (v == null ? "N/A" : formatter(v))} contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.map((s, i) => (
          <Bar key={s.key} dataKey={s.key} name={s.label} fill={colors[i % colors.length]} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
